import { useEffect, useState, useCallback, useRef } from "react";

/**
 * Custom hook to handle DIVE verification via polling
 * Polls the backend API every second to check verification status
 * @param {string} virId - The VIR ID to poll for verification
 * @param {string} authorizationToken - Bearer token from Redux state
 * @param {string} baseUrl - API base URL (athenaURL)
 * @param {string} endpointName - Endpoint name (e.g., 'otherDriver', 'thirdParty', etc.)
 * @param {function} onVerificationSuccess - Callback when verification succeeds
 * @param {function} onVerificationError - Callback when verification fails
 */
export const useDivePolling = (
  virId,
  authorizationToken,
  baseUrl,
  endpointName,
  onVerificationSuccess,
  onVerificationError
) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isWaiting, setIsWaiting] = useState(true);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const pollingIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const MAX_WAIT_TIME = 600; // 10 minutes in seconds

  // Format seconds to MM:SS format
  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const pollVerificationStatus = useCallback(async () => {
    if (!virId || !authorizationToken || !baseUrl || !endpointName) {
      return;
    }

    try {
      const url = `${baseUrl}/vir/dive/${virId}/${endpointName}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authorizationToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data?.data?.state === "success") {
        console.log("[DivePolling] Verification successful");
        setIsWaiting(false);
        if (onVerificationSuccess) {
          onVerificationSuccess(data);
        }
        // Clear polling intervals
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      }
      // Note: If state is "failed", continue polling - DIVE may still be processing
    } catch (err) {
      console.error("[DivePolling] Error:", err);
      // Continue polling on error, don't stop
    }
  }, [virId, authorizationToken, baseUrl, endpointName, onVerificationSuccess, onVerificationError]);

  useEffect(() => {
    if (!virId || !authorizationToken || !baseUrl || !endpointName) {
      console.warn("useDivePolling: virId, authorizationToken, baseUrl, and endpointName are required");
      return;
    }
    console.log("[DivePolling] Effect triggered - Starting polling");
    setIsConnected(true);
    setIsWaiting(true);
    setSecondsElapsed(MAX_WAIT_TIME);

    // Start timer to track elapsed seconds
    timerIntervalRef.current = setInterval(() => {
      setSecondsElapsed((prev) => {
        const newValue = prev - 1;
        console.log("[DivePolling] Timer updated:", newValue);
        // If we've reached 0, stop polling
        if (newValue <= 0) {
          console.warn("[DivePolling] Countdown timer reached 0, stopping polling");
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          setIsWaiting(false);
          setTimedOut(true);
          if (onVerificationError) {
            onVerificationError(
              new Error("Verification timeout: Maximum wait time of 10 minutes exceeded")
            );
          }
          return 0;
        }
        return newValue;
      });
    }, 1000);

    // Start polling every second
    pollingIntervalRef.current = setInterval(() => {
      pollVerificationStatus();
    }, 1000);

    // Initial poll immediately
    pollVerificationStatus();

    // Cleanup
    return () => {
      console.log("[DivePolling] Cleanup - Clearing intervals");
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [virId, authorizationToken, baseUrl, endpointName]);

  const disconnect = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    setIsConnected(false);
    setIsWaiting(false);
    setSecondsElapsed(0);
    setTimedOut(false);
  }, []);

  return {
    isConnected,
    isWaiting,
    secondsElapsed,
    timedOut,
    formattedTime: formatTime(secondsElapsed),
    maxWaitTimeFormatted: formatTime(MAX_WAIT_TIME),
    disconnect,
  };
};

export default useDivePolling;
